import React from 'react';
import styled from 'styled-components/macro';
import { message, Button, List, Modal } from 'antd';
import { LinkOutlined, DeleteOutlined } from '@ant-design/icons';
import { InstitutionalMemoryMetadata } from '../../../../../../types.generated';
import { useEntityData } from '../../../EntityContext';
import { formatDateString } from '../../../containers/profile/utils';
import { useRemoveLinkMutation } from '../../../../../../graphql/mutations.generated';
import { AddLinkModal, LinkType } from '../../../components/styled/AddLinkModal';
import { mapLinkToLinkType } from '../../../utils';
import { sanitizeUrl } from '../../../../../shared/utils/sanitizationUtils';
import { useTranslation } from '../../../../../../i18n/useTranslation';

const LinkListItem = styled(List.Item)`
    border-radius: 4px;
    /* > .ant-btn {
        opacity: 0;
    } */
    &:hover {
        background-color: #f4f6f8;
        /* > .ant-btn {
            opacity: 1;
        } */
    }
`;

type LinkListProps = {
    refetch?: () => Promise<any>;
    readOnly?: boolean;
};

export const LinkList = ({ refetch, readOnly }: LinkListProps) => {
    const { urn: entityUrn, entityData } = useEntityData();
    const { t } = useTranslation();
    // const entityRegistry = useEntityRegistry();
    const [removeLinkMutation] = useRemoveLinkMutation();
    const links = entityData?.institutionalMemory?.elements || [];
    const isReadOnly = readOnly || entityData?.readOnly?.readOnly === true;

    const handleDeleteLink = async (metadata: InstitutionalMemoryMetadata) => {
        try {
            await removeLinkMutation({
                variables: { input: { linkUrl: metadata.url, resourceUrn: metadata.associatedUrn || entityUrn } },
            });
            message.success({ content: t('Link Removed'), duration: 2 });
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error({
                    content: t('Error removing link: {{message}}', { message: e.message || '' }),
                    duration: 2,
                });
            }
        }
        refetch?.();
    };

    const onRemoveLink = (metadata: InstitutionalMemoryMetadata, displayName: string) => {
        Modal.confirm({
            title: t('Remove {{displayName}}', { displayName }),
            content: t('Are you sure you want to remove this link?'),
            onOk() {
                handleDeleteLink(metadata);
            },
            onCancel() {},
            className: 'f-borderless-modal',
            okButtonProps: {
                type: undefined,
                danger: true,
            },
            okText: t('Yes'),
            cancelText: t('Cancel'),
            autoFocusButton: null,
            maskClosable: true,
            centered: true,
        });
    };

    // const isSystem = (name) => name === '__datahub_system';

    // Returns the redirect url incase if user is not system
    // const getDefaultPath = (author) => {
    //     return isSystem(author.username) ? '#' : entityRegistry.getEntityUrl(EntityType.CorpUser, author.urn);
    // };

    // const displayUserName = (name) => {
    //     console.log(name);
    //     if (isSystem(name)) {
    //         return 'System';
    //     }
    //     return name;
    // };

    const existingLinks: LinkType[] = mapLinkToLinkType(links);

    return entityData ? (
        <>
            {links.length > 0 && (
                <List
                    dataSource={links}
                    renderItem={(link, index) => (
                        <LinkListItem
                            extra={
                                !isReadOnly ? (
                                    <>
                                        <AddLinkModal
                                            buttonProps={{ type: 'text' }}
                                            refetch={refetch}
                                            defaultValue={{
                                                link: link.url,
                                                label: link.description || link.label,
                                            }}
                                            key={index}
                                            isEdit
                                            existingLinks={existingLinks}
                                        />
                                        <Button
                                            onClick={() => onRemoveLink(link, link.description || link.label)}
                                            type="text"
                                            shape="circle"
                                            danger
                                        >
                                            <DeleteOutlined />
                                        </Button>
                                    </>
                                ) : null
                            }
                        >
                            <List.Item.Meta
                                title={
                                    <Button type="link" href={sanitizeUrl(link.url)} target="_blank" rel="noreferrer">
                                        <LinkOutlined />
                                        <span>{link.description || link.label}</span>
                                    </Button>
                                }
                                description={
                                    <>
                                        <span className="f-text-content f-color-dark-black-s70">
                                            {t('Added {{date}}', { date: formatDateString(link.created.time) })}
                                        </span>
                                        {/* Hiding Autor name for now */}
                                        {/* <Link className="f-text-content" to={getDefaultPath(link.author)}>
                                            {displayUserName(link.author.username)}
                                        </Link> */}
                                    </>
                                }
                            />
                        </LinkListItem>
                    )}
                />
            )}
        </>
    ) : null;
};
